import React from "react";
import Breadcrumbs from "../components/Breadcrumbs";

interface FAQ {
  question: string;
  answer: string;
}

const FAQ: React.FC = () => {
  // Breadcrumb data
  const breadcrumbItems = [
    {
      label: "Frequently Asked Questions",
      path: "/frequently-asked-questions",
    },
  ];

  const generalFaqs: FAQ[] = [
    {
      question: "What services does PPE.us offer?",
      answer:
        " Dropship orders to any number of locations.\n Overseas sourcing and direct to client importing\n Kitting and branded packaging\n Customized e-commerce platforms for B2C solutions\n Procurement for Government Entities (State, Local, Education, and Federal)\n Free product mockups with your logo",
    },
    {
      question: "What is the order process?",
      answer:
        'Once you place an online order, you will immediately receive an order confirmation. If you are ordering a decorated product, you will receive a virtual proof ("mock up") for your approval and finalized pricing before production begins.',
    },
    {
      question: "Can I See a Sample?",
      answer:
        "Absolutely! Samples are available for almost all of our products. In most cases, samples are free. For more expensive or expedited items, we may ask you to pay for the sample and/or the cost of shipping. If an order is placed, your dedicated consultant will work to help you limit sample costs when possible. Samples are intended to help our clients make an informed buying decision. We reserve the right to refuse any sample request and samples are limited to two per customer. Samples will be blank or will contain a random imprint.",
    },
  ];

  const artworkFaqs: FAQ[] = [
    {
      question:
        "What artwork format do I need? What if I do not have the right kind of file?",
      answer:
        "Decoration requires vector (.ai, .eps, .psd) art or high-resolution pdfs to make sure your design looks perfect. If you do not have the correct type of file, send us what you have. Our consultants will be happy to advise and assist you on the best decoration methods and how to recreate your artwork! Basic art recreations are free.",
    },
    {
      question: "Do you keep my artwork on file?",
      answer:
        "Yes! We keep all active clients' art on file so you can easily reuse the same artwork over and over on any product you like.",
    },
    {
      question: "Can you decorate Apparel?",
      answer:
        "We can decorate your apparel a number of different ways. The most common are screen printing, direct to garment, heat transfer, and embroidery. Your consultant can advise you on the fee structures for each of these decoration methods.",
    },
    {
      question: "Can I choose a PMS color?",
      answer:
        "Of course. For some products, there may be an additional charge to adjust the ink color to match your pantone exactly. Keep in mind that for some decoration techniques or products PMS matching is not always possible. For any techniques, PPE.us will work with our decoration facilities to find the closest available match.",
    },
    {
      question: "What is your policy on the use of trademarks?",
      answer:
        "When you supply PPE.us with artwork, you are warranting that you have the right to use and distribute your artwork. In some cases, PPE.us may require written confirmation from the trademark owner that you are permitted to use it.",
    },
    {
      question: "Will you print anything?",
      answer:
        "PPE.us exists to enhance our clients' brands and their creativity. We are strong proponents of free speech. However, we reserve the right to refuse to print anything that is not protected speech, including but not limited to: obscenity, fighting words, defamation (including libel and slander), child pornography, perjury, blackmail, incitement to imminent lawless action, true threats, solicitation to commit crimes, plagiarism of copyrighted materials, and hate speech. This is entirely at PPE.us discretion.",
    },
  ];

  const paymentFaqs: FAQ[] = [
    {
      question: "What forms of payment do you accept?",
      answer:
        "For first-time buyers, PPE.us accepts most major credit cards, ACH, or wire payments. We also can accept purchase orders from qualifying current clients. If you have already worked with us before, ask your consultant for an PPE.us Credit Application. Please keep in mind that certain products may require prepayment even for current clients.",
    },
    {
      question: "When do you charge my card?",
      answer:
        "If you place an order online, PPE.us will not charge your card until after you have spoken to one of our friendly and fantastic consultants. They will first confirm all of your order details with you to make sure we can execute the order as you requested.",
    },
    {
      question: 'What are "set up" fees?',
      answer:
        "Set up fees depend on the decoration technique. Each technique has its own machinery and work processes. A set up fee is the cost to prepare the machine and any necessary components to print your artwork on your product. Our consultants will be happy to explain more about the techniques available for your specific product and the costs.",
    },
    {
      question: 'What are "run charges"?',
      answer:
        "A run charge is the per unit cost to print on your product. This includes any materials, such as ink or thread, and the labor needed to apply the decoration.",
    },
  ];

  const shippingFaqs: FAQ[] = [
    {
      question: "How fast will I get my order?",
      answer:
        "Each product has its own production time. See the Description section for details on any product. Depending on your location and the shipping method, shipping can take 1-5 days. One of our consultants will be happy to help meet any schedule needs. And if you need something fast, check out our 24 Hour Rush Promotional Products here.",
    },
    {
      question: "Can I split ship to multiple locations?",
      answer:
        "No problem. Just let your promo consultant know where you want to ship, and they will make it happen. If you placed your order online, make sure to let us know when we reach out to confirm your order. Depending on the product, there may be additional fulfillment and shipping fees to ship to multiple locations.",
    },
    {
      question: "Can I ship internationally?",
      answer:
        "Yes, PPE.us can ship anywhere. However, there may be certain types of products that cannot be shipped internationally due to local regulations. Unless otherwise stated, the Buyer is responsible for any duties and/or sales taxes charged during the import process. For easy billing, we recommend shipping on your account.",
    },
    {
      question: "Can I use my own shipping account?",
      answer:
        "Sure! PPE.us works with most major carriers. When you speak to a consultant, let them know your shipping account number.",
    },
    {
      question: "Can I reroute a package during transit?",
      answer:
        "We do not advise rerouting a package during transit as it can create delays. If you absolutely must reroute a package, we will do everything we can to assist you. Rerouting a package may result in additional fees and, in some cases, a package may be too far along to reroute.",
    },
    {
      question: "Why is shipping so expensive for my order?",
      answer:
        "Shipping costs are calculated based on the volume of the package and its weight. Certain types of products may be bulky or heavy. PPE.us also ships some products from overseas directly to our clients. If you are ordering large quantities or heavy items, we may be able to arrange a freight service to help save on costs. Your dedicated consultant will be able to give you guidance on how to best control your shipping costs.",
    },
  ];

  const orderInfoFaqs: FAQ[] = [
    {
      question: "Can I cancel my order?",
      answer:
        "Because most of our products are custom manufactured for you, we are unable to cancel once we start your order. If no decoration has occurred yet, we may be able to return it to the manufacturer for a restocking fee. PPE.us will do everything we can to help our clients control their costs. In the event PPE.us is unable to fulfill your order, your order will be refunded. For more information, please see our Terms of Sale here.",
    },
    {
      question:
        "What if I receive less than I ordered? What if something is broken or not working?",
      answer:
        "PPE.us will replace any missing, defective, or damaged products. And if we are unable to replace it, we will refund you the difference. PPE.us asks that clients report any incorrect, missing, or damaged products within 3 business days of receiving their order. For defective products, please notify us within 30 days of receipt. Depending on the nature of the issue, we may also ask you to provide photos.",
    },
    {
      question: "What if I find the same product somewhere else for less?",
      answer:
        "PPE.us has a policy to beat or match any price we are able to. If you find the same product for a lower total cost, tell us! We want to work with you and will do everything we can to work within your budget.",
    },
    {
      question: "What if I am unhappy with how my order turned out?",
      answer:
        "We encourage our clients to take advantage of our generous sample policy so you can see the product before you buy. If you think something looks wrong when you receive your order, please tell us! We want you to be happy with your order and will do everything we can to make it right.",
    },
    {
      question: "What is PPE.us Privacy Policy?",
      answer:
        "PPE.us respects your privacy with the highest regards. For more information on our Privacy Policy, please see the link below. https://www.ipromo.com/promo-products/20029/Privacy-Policy.html",
    },
  ];

  const renderFaqSection = (title: string, faqs: FAQ[]) => (
    <div className="">
      <h3 className="text-[17px] font-semibold leading-[1.1] mt-[18px] mb-[9px]">
        {title}
      </h3>
      <div className="space-y-4">
        {faqs.map((faq, index) => (
          <div key={index}>
            <p
              className="font-normal text-[#777] mb-2"
              style={{ fontSize: "13px" }}
            >
              {faq.question}
            </p>
            <div
              className="text-[#777] mb-2"
              style={{ fontSize: "13px", lineHeight: "1.6" }}
            >
              {faq.question === "What services does PPE.us offer?" ? (
                <ul className="pl-10 list-disc">
                  {faq.answer
                    .split("\n")
                    .map((line: string, lineIndex: number) => (
                      <li
                        key={lineIndex}
                        className={lineIndex > 0 ? "mt-2" : ""}
                      >
                        {line}
                      </li>
                    ))}
                </ul>
              ) : (
                faq.answer
                  .split("\n")
                  .map((line: string, lineIndex: number) => (
                    <p key={lineIndex} className={lineIndex > 0 ? "mt-2" : ""}>
                      {line}
                    </p>
                  ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <div className="flex flex-col">
      <main className="flex-grow">
        {/* Breadcrumbs */}
        <Breadcrumbs items={breadcrumbItems} />

        {/* FAQ Content */}
        <div className="w-full pt-[15px] pb-[86px] bg-white">
          <div className="px-5 max-w-[1710px] mx-auto">
            <h1 className="font-normal mb-[15px] text-[#0088CC] text-[20px]">
              Frequently Asked Questions
            </h1>

            {renderFaqSection("General", generalFaqs)}
            {renderFaqSection("Artwork", artworkFaqs)}
            {renderFaqSection("Payment", paymentFaqs)}
            {renderFaqSection("Shipping", shippingFaqs)}
            {renderFaqSection("Order Info", orderInfoFaqs)}
          </div>
        </div>
      </main>
    </div>
  );
};

export default FAQ;
